import { openDB, type IDBPDatabase } from 'idb';
import type { Direction, KeyPairType, StorageType } from '@privacyresearch/libsignal-protocol-typescript';

/**
 * Schlüsselspeicher im Browser (IndexedDB).
 *
 * Hier liegt alles, was der Server niemals sehen darf: Identity Key, Prekeys
 * und der laufende Double-Ratchet-Zustand. Es gibt bewusst keine Funktion,
 * die diesen Speicher exportiert — ein Export wäre der direkte Weg, die
 * Ende-zu-Ende-Eigenschaft zu verlieren.
 *
 * Konsequenz, die im UI kommuniziert werden muss: Löscht der Nutzer seine
 * Browserdaten, sind Schlüssel und Verlauf weg. Ohne verschlüsseltes Backup
 * gibt es keine Wiederherstellung.
 */

const DB_NAME = 'webmessenger';
const DB_VERSION = 1;

export type StoredMessage = {
  id: string;
  peerDeviceId: string;
  direction: 'in' | 'out';
  text: string;
  timestamp: number;
  /**
   * Bei eigenen Nachrichten: ob die Gegenseite sie geoeffnet hat.
   * Bei eingehenden: ob WIR die Bestaetigung schon geschickt haben.
   *
   * Fehlt das Feld, stammt der Eintrag aus der Zeit vor den
   * Lesebestaetigungen — dann gilt 'sent', nicht 'gelesen'.
   */
  status?: 'sent' | 'read';
  /**
   * Systemeintrag statt Nachricht — etwa die Warnung bei einem
   * Schluesselwechsel. Steht dauerhaft im Verlauf und laesst sich nicht
   * wegklicken: Genau das verlangt docs/03-bedrohungsmodell.md §5.
   */
  system?: boolean;
  /**
   * Nur bei eingehenden Nachrichten: die Kennung, unter der der ABSENDER
   * diese Nachricht fuehrt. Nur sie darf in einer Bestaetigung stehen; die
   * lokale `id` ist die Envelope-Kennung und beim Absender unbekannt.
   */
  remoteId?: string;
};

let dbPromise: Promise<IDBPDatabase> | null = null;

function db(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      // Schlüsselmaterial und Sitzungszustand.
      database.createObjectStore('signal');
      // Klartext-Verlauf. Existiert ausschließlich auf diesem Gerät.
      const messages = database.createObjectStore('messages', { keyPath: 'id' });
      messages.createIndex('byPeer', 'peerDeviceId');
    },
  });
  return dbPromise;
}

async function get<T>(key: string): Promise<T | undefined> {
  return (await db()).get('signal', key) as Promise<T | undefined>;
}

async function put(key: string, value: unknown): Promise<void> {
  await (await db()).put('signal', value, key);
}

async function del(key: string): Promise<void> {
  await (await db()).delete('signal', key);
}

/**
 * Implementiert das Speicher-Interface der Signal-Bibliothek.
 * Die Bibliothek ruft diese Methoden selbst auf; sie werden nicht direkt
 * aus der Anwendung benutzt.
 */
export class IndexedDbSignalStore implements StorageType {
  async getIdentityKeyPair(): Promise<KeyPairType | undefined> {
    return get<KeyPairType>('identityKey');
  }

  async getLocalRegistrationId(): Promise<number | undefined> {
    return get<number>('registrationId');
  }

  async setIdentity(keyPair: KeyPairType, registrationId: number): Promise<void> {
    await put('identityKey', keyPair);
    await put('registrationId', registrationId);
  }

  /**
   * Vertrauensentscheidung für den Schlüssel einer Gegenstelle.
   *
   * Beim ersten Kontakt wird der Schlüssel akzeptiert (Trust On First Use).
   * Ändert er sich später, wird die Nachricht NICHT still angenommen — das
   * wäre genau der Fall, in dem ein bösartiger Server ein eigenes Gerät
   * untergeschoben hat. Die Anwendung muss den Nutzer warnen; siehe
   * `hasIdentityChanged`.
   */
  async isTrustedIdentity(
    identifier: string,
    identityKey: ArrayBuffer,
    _direction: Direction,
  ): Promise<boolean> {
    const known = await get<ArrayBuffer>(`identity.${identifier}`);
    if (!known) return true;
    return buffersEqual(known, identityKey);
  }

  async loadIdentityKey(identifier: string): Promise<ArrayBuffer | undefined> {
    return get<ArrayBuffer>(`identity.${identifier}`);
  }

  async saveIdentity(identifier: string, identityKey: ArrayBuffer): Promise<boolean> {
    const existing = await get<ArrayBuffer>(`identity.${identifier}`);
    await put(`identity.${identifier}`, identityKey);
    // true = der Schlüssel hat sich geändert. Die Anwendung wertet das als
    // Warnung an den Nutzer aus, nicht als Fehler.
    return existing !== undefined && !buffersEqual(existing, identityKey);
  }

  async loadPreKey(keyId: string | number): Promise<KeyPairType | undefined> {
    return get<KeyPairType>(`preKey.${keyId}`);
  }

  async storePreKey(keyId: string | number, keyPair: KeyPairType): Promise<void> {
    await put(`preKey.${keyId}`, keyPair);
  }

  /** One-Time-Prekeys werden nach Gebrauch gelöscht — daher "one time". */
  async removePreKey(keyId: string | number): Promise<void> {
    await del(`preKey.${keyId}`);
  }

  async loadSignedPreKey(keyId: string | number): Promise<KeyPairType | undefined> {
    return get<KeyPairType>(`signedPreKey.${keyId}`);
  }

  async storeSignedPreKey(keyId: string | number, keyPair: KeyPairType): Promise<void> {
    await put(`signedPreKey.${keyId}`, keyPair);
  }

  async removeSignedPreKey(keyId: string | number): Promise<void> {
    await del(`signedPreKey.${keyId}`);
  }

  /** Der Double-Ratchet-Zustand. Ändert sich mit JEDER Nachricht. */
  async loadSession(identifier: string): Promise<string | undefined> {
    return get<string>(`session.${identifier}`);
  }

  async storeSession(identifier: string, record: string): Promise<void> {
    await put(`session.${identifier}`, record);
  }

  /**
   * Den gespeicherten Schluessel einer Gegenstelle vergessen.
   *
   * Nur nach ausdruecklicher Bestaetigung eines Schluesselwechsels. Danach
   * gilt beim naechsten Kontakt wieder Trust On First Use.
   */
  async removeIdentity(identifier: string): Promise<void> {
    await del(`identity.${identifier}`);
  }

  async removeSession(identifier: string): Promise<void> {
    await del(`session.${identifier}`);
  }

  async hasSession(identifier: string): Promise<boolean> {
    return (await get<string>(`session.${identifier}`)) !== undefined;
  }
}

function buffersEqual(a: ArrayBuffer, b: ArrayBuffer): boolean {
  if (a.byteLength !== b.byteLength) return false;
  const x = new Uint8Array(a);
  const y = new Uint8Array(b);
  for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return false;
  return true;
}

/* ── Lokaler Nachrichtenverlauf ─────────────────────────────────────────── */

export async function saveMessage(message: StoredMessage): Promise<void> {
  await (await db()).put('messages', message);
}

export async function loadConversation(peerDeviceId: string): Promise<StoredMessage[]> {
  const all = (await (await db()).getAllFromIndex(
    'messages',
    'byPeer',
    peerDeviceId,
  )) as StoredMessage[];
  return all.sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Eigene Nachrichten als gelesen markieren.
 *
 * Gibt zurueck, ob sich etwas geaendert hat. Der Aufrufer spart sich damit ein
 * unnoetiges Neuladen des Verlaufs, wenn eine Bestaetigung doppelt eintrifft —
 * und doppelt kommen sie, sobald die Gegenseite mehrere Geraete hat.
 */
export async function markMessagesRead(messageIds: string[]): Promise<boolean> {
  if (messageIds.length === 0) return false;

  const database = await db();
  const tx = database.transaction('messages', 'readwrite');
  let changed = false;

  for (const id of messageIds) {
    const message = (await tx.store.get(id)) as StoredMessage | undefined;
    // Nur eigene Nachrichten: Eine Bestaetigung fuer eine EINGEHENDE Nachricht
    // waere sinnlos und liesse sich von der Gegenseite missbrauchen, um
    // fremde Eintraege im lokalen Verlauf zu veraendern.
    if (!message || message.direction !== 'out' || message.status === 'read') continue;
    await tx.store.put({ ...message, status: 'read' });
    changed = true;
  }

  await tx.done;
  return changed;
}

/**
 * Eingehende Nachrichten eines Gespraechs als bestaetigt markieren und die
 * Kennungen zurueckgeben, die der Absender kennt.
 *
 * In einem Zug lesen UND markieren: Sonst schickt ein zweiter Aufruf — etwa
 * weil der Abholzyklus kurz hintereinander laeuft — dieselbe Bestaetigung
 * erneut. Das waere zusaetzlicher Verkehr ohne Nutzen und fuer den Server ein
 * weiteres Signal.
 */
export async function takeUnconfirmed(peerKey: string): Promise<string[]> {
  const database = await db();
  const tx = database.transaction('messages', 'readwrite');
  const all = (await tx.store.index('byPeer').getAll(peerKey)) as StoredMessage[];
  const ids: string[] = [];

  for (const message of all) {
    if (message.direction !== 'in' || !message.remoteId || message.status === 'read') continue;
    ids.push(message.remoteId);
    await tx.store.put({ ...message, status: 'read' });
  }

  await tx.done;
  return ids;
}

/** Vollständiges Zurücksetzen: Schlüssel und Verlauf. Nicht umkehrbar. */
export async function wipeEverything(): Promise<void> {
  const database = await db();
  await database.clear('signal');
  await database.clear('messages');
}

/** Letzte Nachricht eines Gesprächs — für die Vorschau in der Chatliste. */
export async function loadLastMessage(peerDeviceId: string): Promise<StoredMessage | null> {
  const all = await loadConversation(peerDeviceId);
  return all.length > 0 ? all[all.length - 1]! : null;
}
